import { collection, addDoc, getDocs, query, where, orderBy, limit, doc, getDoc, runTransaction } from 'firebase/firestore';
import { db } from '../firebase/config';
import { calculateMultipliedPoints, calculatePointMultiplier, calculateRank } from '../types/user';

export interface BountySubmission {
  bountyId: string;
  walletAddress: string;
  username: string;
  solution: string;
  timestamp: string;
  pointsAwarded?: number;
  basePoints?: number;
  multiplier?: number;
}

const BOUNTY_SUBMISSION_POINTS = 50;

export const submitBountySolution = async (submission: BountySubmission): Promise<string> => {
  try {
    // Create a reference to the bounty's submissions subcollection
    const submissionsRef = collection(db, 'bounties', submission.bountyId, 'submissions');
    
    // Check if user has already submitted
    const existingSubmissions = await getDocs(
      query(
        submissionsRef,
        where('walletAddress', '==', submission.walletAddress)
      )
    );
    
    if (!existingSubmissions.empty) {
      throw new Error('You have already submitted a solution for this bounty');
    }

    // Start a transaction to update both submissions and user points
    const userRef = doc(db, 'users', submission.walletAddress.toLowerCase());
    
    await runTransaction(db, async (transaction) => {
      // Get current user data
      const userDoc = await transaction.get(userRef);
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const userData = userDoc.data();
      const currentPoints = userData.totalPoints || 0;
      const stakedAmount = userData.stakedAmount || 0;

      // Calculate points with multiplier
      const pointsToAdd = calculateMultipliedPoints(BOUNTY_SUBMISSION_POINTS, stakedAmount);
      const multiplier = calculatePointMultiplier(stakedAmount);
      
      // Calculate new total points and rank
      const newTotalPoints = currentPoints + pointsToAdd;
      const newRank = calculateRank(newTotalPoints);
      
      // Add submission with detailed point information
      const submissionRef = doc(submissionsRef);
      transaction.set(submissionRef, {
        ...submission,
        timestamp: new Date().toISOString(),
        pointsAwarded: pointsToAdd,
        basePoints: BOUNTY_SUBMISSION_POINTS,
        multiplier: multiplier
      });

      // Update user points and rank
      transaction.update(userRef, {
        totalPoints: newTotalPoints,
        rank: newRank,
        lastUpdated: new Date().toISOString()
      });

      return submissionRef.id;
    });

    return 'Submission successful';
  } catch (error) {
    console.error('Error submitting bounty solution:', error);
    throw error;
  }
};

export const getBountySubmissions = async (bountyId: string): Promise<BountySubmission[]> => {
  try {
    const submissionsRef = collection(db, 'bounties', bountyId, 'submissions');
    const q = query(submissionsRef, orderBy('timestamp', 'desc'));
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      ...doc.data() as BountySubmission,
      id: doc.id
    }));
  } catch (error) {
    console.error('Error getting bounty submissions:', error);
    return [];
  }
};